import styled from '@emotion/styled';
import React, { useCallback } from 'react';

/**
 * Цвет чипа по исходу заявки. «neutral» — чип «Все»: он не про исход, поэтому
 * активным красится жёлтым, как остальные фильтры админки.
 */
export type ApplicationStatusChipTone = 'neutral' | 'pending' | 'accepted' | 'declined';

export interface ApplicationStatusChipOption<T extends string> {
  value: T;
  label: string;
  count: number;
  tone?: ApplicationStatusChipTone;
}

interface ApplicationStatusChipsProps<T extends string> {
  options: ApplicationStatusChipOption<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
}

/**
 * Класс счётчика: окрашенный чип красит его цветом своего тона. Обратиться к Count
 * component selector'ом нельзя — в проекте нет @emotion/babel-plugin
 * (.claude/rules/conventions.md), поэтому адресуемся по классу.
 */
const COUNT_CLASS = 'application-status-chip-count';

/**
 * Чипы статусов заявок: «Все / Ожидают решения / Заявлены / Отклонены».
 *
 * Настоящие `<button>`, а не `div`, как фильтр старого флоу
 * (`championship-application-filters/filter-button.tsx:4`): состояние читается
 * скринридером через `aria-pressed`, а тест находит чип по роли.
 *
 * Тон выбирается селектором по `data-tone`: цвет — это оформление состояния, а не
 * отдельный проп стилей, и в разметке остаётся признак, который видно и в тесте,
 * и в devtools.
 *
 * Чип статуса окрашен всегда: неактивный — светлой заливкой своего тона, активный —
 * насыщенной, с границей цвета текста. Так три исхода различимы, не выбирая ни
 * одного из них. Нейтральный «Все» собственного исхода не имеет и красится как
 * остальные фильтры админки — жёлтым акцентом.
 */
const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.colors.WhiteGray20};
  background-color: ${({ theme }) => theme.colors.SurfaceWhite};
  color: ${({ theme }) => theme.colors.Gray52};
  white-space: nowrap;
  transition:
    background-color 0.2s ease-in-out,
    border-color 0.2s ease-in-out;

  &[data-tone='pending'] {
    background-color: ${({ theme }) => theme.colors.CasePendingSurface};
    border-color: ${({ theme }) => theme.colors.CasePending};
    color: ${({ theme }) => theme.colors.CasePendingText};
  }

  &[data-tone='accepted'] {
    background-color: ${({ theme }) => theme.colors.CaseSuccessSurface};
    border-color: ${({ theme }) => theme.colors.CaseSuccess};
    color: ${({ theme }) => theme.colors.CaseSuccessText};
  }

  &[data-tone='declined'] {
    background-color: ${({ theme }) => theme.colors.CaseErrorSurface};
    border-color: ${({ theme }) => theme.colors.CaseError};
    color: ${({ theme }) => theme.colors.CaseErrorText};
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.AccentNaplesYellow};
  }

  &[data-tone='pending']:hover,
  &[data-tone='accepted']:hover,
  &[data-tone='declined']:hover {
    border-color: currentColor;
  }

  &[aria-pressed='true'] {
    background-color: ${({ theme }) => theme.colors.AccentCornsilk};
    border-color: ${({ theme }) => theme.colors.AccentNaplesYellow};
    color: ${({ theme }) => theme.colors.SurfaceRaisinBlack50};
    font-weight: 500;
  }

  &[aria-pressed='true'][data-tone='pending'] {
    background-color: ${({ theme }) => theme.colors.CasePendingSurfaceActive};
    border-color: ${({ theme }) => theme.colors.CasePendingText};
    color: ${({ theme }) => theme.colors.CasePendingText};
  }

  &[aria-pressed='true'][data-tone='accepted'] {
    background-color: ${({ theme }) => theme.colors.CaseSuccessSurfaceActive};
    border-color: ${({ theme }) => theme.colors.CaseSuccessText};
    color: ${({ theme }) => theme.colors.CaseSuccessText};
  }

  &[aria-pressed='true'][data-tone='declined'] {
    background-color: ${({ theme }) => theme.colors.CaseErrorSurfaceActive};
    border-color: ${({ theme }) => theme.colors.CaseErrorText};
    color: ${({ theme }) => theme.colors.CaseErrorText};
  }

  &[data-tone='pending']
    .${COUNT_CLASS},
    &[data-tone='accepted']
    .${COUNT_CLASS},
    &[data-tone='declined']
    .${COUNT_CLASS},
    &[aria-pressed='true']
    .${COUNT_CLASS} {
    color: inherit;
    opacity: 0.75;
  }
`;

const Count = styled.span`
  color: ${({ theme }) => theme.colors.SurfaceRomanSilver30};
`;

function ApplicationStatusChip<T extends string>({
  option,
  isActive,
  onChange,
}: {
  option: ApplicationStatusChipOption<T>;
  isActive: boolean;
  onChange: (value: T) => void;
}) {
  const handleClick = useCallback(() => onChange(option.value), [onChange, option.value]);

  return (
    <Chip type="button" aria-pressed={isActive} data-tone={option.tone ?? 'neutral'} onClick={handleClick}>
      <span>{option.label}</span>
      <Count className={COUNT_CLASS}>{option.count}</Count>
    </Chip>
  );
}

function ApplicationStatusChips<T extends string>({ options, value, onChange, className }: ApplicationStatusChipsProps<T>) {
  return (
    <div className={className} role="group">
      <div className="flex flex-wrap items-center gap-2">
        {options.map((option) => (
          <ApplicationStatusChip key={option.value} option={option} isActive={option.value === value} onChange={onChange} />
        ))}
      </div>
    </div>
  );
}

export default ApplicationStatusChips;
